import { Box, Container, Typography } from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'

const PrivacyPolicy = () => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', py: 8 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Box sx={{ mb: 6 }}>
          <Typography
            component={RouterLink}
            to="/"
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              textDecoration: 'none',
              display: 'inline-block',
              mb: 4,
            }}
          >
            Mocksy
          </Typography>

          <Typography variant="h3" sx={{ fontWeight: 700, color: 'grey.900', mb: 2 }}>
            Privacy Policy
          </Typography>
          <Typography variant="body1" sx={{ color: 'grey.600' }}>
            Last Updated: December 13, 2024
          </Typography>
        </Box>

        {/* Content */}
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: 2,
            p: 4,
            boxShadow: 1,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            1. Introduction
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            Welcome to Mocksy ("we," "our," or "us"). We are committed to protecting your personal
            information and your right to privacy. This Privacy Policy explains how we collect, use,
            disclose, and safeguard your information when you use our AI-powered product image
            generation service.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            2. Information We Collect
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'grey.700', lineHeight: 1.7 }}>
            We collect information that you provide directly to us, including:
          </Typography>
          <Box component="ul" sx={{ mb: 4, pl: 3, color: 'grey.700', lineHeight: 1.7 }}>
            <li>Account information (name, email address, password)</li>
            <li>Payment information (processed securely through Stripe)</li>
            <li>Images you upload for processing</li>
            <li>Prompts and descriptions you provide</li>
            <li>Usage data and analytics</li>
            <li>Device and browser information</li>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            3. How We Use Your Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'grey.700', lineHeight: 1.7 }}>
            We use your information to:
          </Typography>
          <Box component="ul" sx={{ mb: 4, pl: 3, color: 'grey.700', lineHeight: 1.7 }}>
            <li>Provide, maintain, and improve our services</li>
            <li>Process your AI image generation requests</li>
            <li>Process payments and manage subscriptions</li>
            <li>Send you technical notices and support messages</li>
            <li>Respond to your comments and questions</li>
            <li>Detect and prevent fraud and abuse</li>
            <li>Comply with legal obligations</li>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            4. Information Sharing and Disclosure
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            We do not sell your personal information. We may share your information with:
          </Typography>
          <Box component="ul" sx={{ mb: 4, pl: 3, color: 'grey.700', lineHeight: 1.7 }}>
            <li>
              <strong>Service Providers:</strong> Third-party vendors who perform services on our
              behalf (e.g., Google Gemini for AI processing, Stripe for payments, Stytch for
              authentication)
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law or to protect our rights
            </li>
            <li>
              <strong>Business Transfers:</strong> In connection with a merger, sale, or acquisition
            </li>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            5. Data Security
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            We implement appropriate technical and organizational measures to protect your personal
            information. However, no method of transmission over the Internet is 100% secure, and we
            cannot guarantee absolute security.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            6. Data Retention
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            We retain your personal information for as long as necessary to provide our services and
            fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is
            required by law.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            7. Your Rights
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'grey.700', lineHeight: 1.7 }}>
            Depending on your location, you may have certain rights regarding your personal
            information, including:
          </Typography>
          <Box component="ul" sx={{ mb: 4, pl: 3, color: 'grey.700', lineHeight: 1.7 }}>
            <li>Access to your personal information</li>
            <li>Correction of inaccurate data</li>
            <li>Deletion of your data</li>
            <li>Objection to processing</li>
            <li>Data portability</li>
            <li>Withdrawal of consent</li>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            8. Cookies and Tracking
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            We use cookies and similar tracking technologies to collect information about your
            browsing activities. You can control cookies through your browser settings.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            9. Children's Privacy
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            Our services are not intended for children under 13 years of age. We do not knowingly
            collect personal information from children under 13.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            10. Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" sx={{ mb: 4, color: 'grey.700', lineHeight: 1.7 }}>
            We may update this Privacy Policy from time to time. We will notify you of any changes by
            posting the new Privacy Policy on this page and updating the "Last Updated" date.
          </Typography>

          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: 'grey.900' }}>
            11. Contact Us
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: 'grey.700', lineHeight: 1.7 }}>
            If you have questions about this Privacy Policy, please contact us at:
          </Typography>
          <Typography variant="body1" sx={{ color: 'grey.700', lineHeight: 1.7 }}>
            Email: privacy@getmocksy.com
          </Typography>
        </Box>

        {/* Back to Home */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography
            component={RouterLink}
            to="/"
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            ← Back to Home
          </Typography>
        </Box>
      </Container>
    </Box>
  )
}

export default PrivacyPolicy
